import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Sparkles,
  MapPin,
  Users,
  Heart,
  Clock,
  Film,
  Edit3,
  Wand2,
  ChevronDown,
  ChevronUp,
  Check,
  Loader2,
  Zap
} from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * VideoPreviewModal - Shows enhanced prompt preview before video creation
 * 
 * Props:
 * - isOpen: boolean - Whether modal is visible
 * - onClose: () => void - Close handler
 * - previewData: object - Data from /api/igoassist/auto-enhance
 * - onProceed: (enhancedPrompt: string) => void - Called when user clicks "Create Video"
 * - onOpenFullOptimizer: () => void - Opens the full iGoAssist optimizer
 * - isCreating: boolean - Whether video is being created
 */
const VideoPreviewModal = ({
  isOpen,
  onClose,
  previewData,
  onProceed,
  onOpenFullOptimizer,
  isCreating = false
}) => {
  const [editedPrompt, setEditedPrompt] = useState(previewData?.enhanced_prompt || '');
  const [isEditing, setIsEditing] = useState(false);
  const [showChanges, setShowChanges] = useState(false);

  // Update edited prompt when preview data changes
  React.useEffect(() => {
    if (previewData?.enhanced_prompt) {
      setEditedPrompt(previewData.enhanced_prompt);
    }
  }, [previewData?.enhanced_prompt]);

  if (!isOpen || !previewData) return null;

  const {
    detected_scene,
    detected_characters,
    detected_mood,
    estimated_duration,
    video_style,
    changes_made,
    summary,
    recommended_mode
  } = previewData;

  // Mood emoji mapping
  const moodEmojis = {
    romantic: '💕',
    tense: '😰',
    comedic: '😄',
    suspenseful: '😨',
    neutral: '😊',
    emotional: '🥺',
    dramatic: '🎭',
    playful: '😜'
  };

  // Style icons
  const styleIcons = {
    dialogue: <Users className="w-4 h-4" />,
    action: <Zap className="w-4 h-4" />,
    product: <Film className="w-4 h-4" />,
    aesthetic: <Sparkles className="w-4 h-4" />,
    multi_dialogue: <Users className="w-4 h-4" />,
    single_dialogue: <Users className="w-4 h-4" />
  };

  const getMoodEmoji = (mood) => {
    const moodLower = (mood || '').toLowerCase();
    for (const [key, emoji] of Object.entries(moodEmojis)) {
      if (moodLower.includes(key)) return emoji;
    }
    return '🎬';
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25 }}
          className="bg-gradient-to-br from-[#1a1a2e] to-[#16162a] border border-[#00F0FF]/20 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="video-preview-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-[#00F0FF]/10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#00F0FF]/10 rounded-lg">
                <Sparkles className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Video Preview</h2>
                <p className="text-sm text-gray-400">Review before creating</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="preview-modal-close"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 space-y-5 overflow-y-auto max-h-[calc(90vh-180px)]">
            {/* Summary */}
            {summary && (
              <div className="bg-[#00F0FF]/5 border border-[#00F0FF]/20 rounded-xl p-4">
                <p className="text-white text-sm leading-relaxed">
                  <span className="text-[#00F0FF] font-medium">Based on your description: </span>
                  {summary}
                </p>
              </div>
            )}

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Scene */}
              <div className="bg-white/5 rounded-xl p-3 space-y-1">
                <div className="flex items-center gap-2 text-gray-400 text-xs">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>Scene</span>
                </div>
                <p className="text-white text-sm font-medium truncate" title={detected_scene}>
                  {detected_scene?.split(',')[0] || 'Auto-detected'}
                </p>
              </div>

              {/* Characters */}
              <div className="bg-white/5 rounded-xl p-3 space-y-1">
                <div className="flex items-center gap-2 text-gray-400 text-xs">
                  <Users className="w-3.5 h-3.5" />
                  <span>Characters</span>
                </div>
                <p className="text-white text-sm font-medium">
                  {detected_characters?.length || 1} {detected_characters?.length === 1 ? 'person' : 'people'}
                </p>
              </div>

              {/* Mood */}
              <div className="bg-white/5 rounded-xl p-3 space-y-1">
                <div className="flex items-center gap-2 text-gray-400 text-xs">
                  <Heart className="w-3.5 h-3.5" />
                  <span>Mood</span>
                </div>
                <p className="text-white text-sm font-medium flex items-center gap-1.5">
                  <span>{getMoodEmoji(detected_mood)}</span>
                  <span className="capitalize">{detected_mood?.split('/')[0] || 'Neutral'}</span>
                </p>
              </div>

              {/* Duration */}
              <div className="bg-white/5 rounded-xl p-3 space-y-1">
                <div className="flex items-center gap-2 text-gray-400 text-xs">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Duration</span>
                </div>
                <p className="text-white text-sm font-medium">
                  ~{estimated_duration || 15}s
                </p>
              </div>
            </div>

            {/* Video Style & Recommended Mode */}
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="outline" className="border-[#00F0FF]/30 text-[#00F0FF] bg-[#00F0FF]/5 px-3 py-1">
                {styleIcons[video_style] || <Film className="w-4 h-4" />}
                <span className="ml-1.5 capitalize">{video_style?.replace('_', ' ') || 'Video'}</span>
              </Badge>
              <Badge 
                variant="outline" 
                className={`px-3 py-1 ${
                  recommended_mode === 'premium' 
                    ? 'border-purple-500/30 text-purple-400 bg-purple-500/5' 
                    : 'border-amber-500/30 text-amber-400 bg-amber-500/5'
                }`}
              >
                {recommended_mode === 'premium' ? <Sparkles className="w-3.5 h-3.5" /> : <Zap className="w-3.5 h-3.5" />}
                <span className="ml-1.5 capitalize">{recommended_mode || 'Premium'} recommended</span>
              </Badge>
            </div>

            {/* Enhanced Prompt */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                  <Edit3 className="w-4 h-4" />
                  Enhanced Prompt
                </label>
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="text-xs text-[#00F0FF] hover:text-[#00F0FF]/80 transition-colors flex items-center gap-1"
                  data-testid="toggle-edit-prompt"
                >
                  {isEditing ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Done
                    </>
                  ) : (
                    <>
                      <Edit3 className="w-3.5 h-3.5" />
                      Edit
                    </>
                  )}
                </button>
              </div>
              
              {isEditing ? (
                <Textarea
                  value={editedPrompt}
                  onChange={(e) => setEditedPrompt(e.target.value)}
                  className="min-h-[120px] bg-white/5 border-[#00F0FF]/20 focus:border-[#00F0FF]/50 text-sm"
                  data-testid="edit-prompt-textarea"
                />
              ) : (
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-sm text-gray-300 whitespace-pre-wrap max-h-[150px] overflow-y-auto">
                  {editedPrompt}
                </div>
              )}
            </div>

            {/* Changes Made (Collapsible) */}
            {changes_made && changes_made.length > 0 && (
              <div className="border border-white/10 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowChanges(!showChanges)}
                  className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
                  data-testid="toggle-changes"
                >
                  <span className="text-sm text-gray-400 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#00F0FF]" />
                    {changes_made.length} enhancement{changes_made.length > 1 ? 's' : ''} applied
                  </span>
                  {showChanges ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                
                <AnimatePresence>
                  {showChanges && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-white/10"
                    >
                      <ul className="p-3 space-y-1.5">
                        {changes_made.map((change, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-gray-400">
                            <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                            {change}
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Full Optimizer Link */}
            <div className="flex items-center justify-center">
              <button
                onClick={onOpenFullOptimizer}
                className="text-sm text-gray-400 hover:text-[#00F0FF] transition-colors flex items-center gap-2"
                data-testid="open-full-optimizer"
              >
                <Wand2 className="w-4 h-4" />
                Want more dramatic changes? Open Full Optimizer
              </button>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between p-5 border-t border-[#00F0FF]/10 bg-black/20">
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-gray-400 hover:text-white"
              data-testid="preview-cancel-btn"
            >
              Cancel
            </Button>
            
            <Button
              onClick={() => onProceed(editedPrompt)}
              disabled={isCreating || !editedPrompt.trim()}
              className="bg-gradient-to-r from-[#00F0FF] to-[#00D4FF] hover:from-[#00D4FF] hover:to-[#00F0FF] text-black font-semibold px-6"
              data-testid="preview-create-btn"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Film className="w-4 h-4 mr-2" />
                  Create Video
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VideoPreviewModal;
